// frontend/src/components/Playbook.tsx
//
// The written rulebook behind the Trade Desk: how a day trader reads the
// chart, which setups we take, the candle / chart patterns the engine
// detects (playbook/PatternLibrary), the exact entry / stop / target rules,
// and the risk rules that keep an account alive. Numbers quoted here come
// from backend/backtest-results (net of costs).
//
// Layout (2026-10-05, from Mobbin article/docs screens — Plain, Hashnode,
// Codecademy, Linear, Matter): one 42.5rem reading column (~75 characters a
// line) in the `.reading` type style from index.css — Inter 16px / 1.7,
// near-black prose — with numbered section headers, figures above their
// text, and a sticky "On this page" list on wide screens (xl+).

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Alert, Button, Chip, Drawer, Kbd, Table, buttonVariants } from "@heroui/react";
import { Crosshair, TrendingUp, Ban, Shield, Clock, Calculator, TableOfContents } from "lucide-react";
import { cn } from "@/lib/utils";
import { Candle } from "./playbook/Candle";
import { PatternLibrary } from "./playbook/PatternLibrary";
import { SignalRules } from "./playbook/SignalRules";

const TOC = [
  { id: "funnel", label: "The funnel" },
  { id: "rules", label: "Every rule, explained", sub: true },
  { id: "setups", label: "The setups" },
  { id: "patterns", label: "Trading patterns" },
  { id: "managing", label: "Managing the trade" },
  { id: "risk", label: "Risk" },
  { id: "chart", label: "Reading the chart" },
  { id: "structure", label: "Market structure" },
  { id: "timeframes", label: "Why 1h entries" },
  { id: "routine", label: "A day at the desk" },
  { id: "habits", label: "What profitable traders do" },
  { id: "glossary", label: "Words on the desk" },
  { id: "day-trading", label: "Day trading" },
] as const;

/** Numbered sections only: a `sub` entry is a part of the section above it. */
const SECTION_COUNT = TOC.filter((t) => !("sub" in t)).length;
/** The section number a TOC entry shows (sub entries show none). */
const tocNumber = (i: number) => ("sub" in TOC[i]! ? null : TOC.slice(0, i + 1).filter((t) => !("sub" in t)).length);

export function Playbook() {
  const active = useScrollSpy(TOC.map((t) => t.id));
  const articleRef = useRef<HTMLElement>(null);
  const minutes = useReadingMinutes(articleRef);

  const [tocOpen, setTocOpen] = useState(false);

  // Deep links from the Simple desk's "Learn the basics" cards (/playbook#risk).
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    return () => clearTimeout(id);
  }, [hash]);

  return (
    <div className="mx-auto grid w-full max-w-6xl gap-16 pb-24 xl:grid-cols-[minmax(0,42.5rem)_13rem] xl:justify-center">
      <article ref={articleRef} className="reading mx-auto w-full min-w-0 max-w-[42.5rem] pt-4">
        {/* ── Title ──────────────────────────────────────────────────────── */}
        <header>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted">
              <span className="font-semibold text-accent-soft-foreground">Playbook</span>
              <span aria-hidden> · </span>
              {SECTION_COUNT} sections
              {minutes > 0 && <><span aria-hidden> · </span>{minutes} min read</>}
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="xl:hidden" onPress={() => setTocOpen(true)}>
                <TableOfContents className="size-3.5" /> Contents
              </Button>
              <Link to="/" className={buttonVariants({ variant: "outline", size: "sm" })}>
                <Crosshair className="size-3.5" /> Back to desk
              </Link>
            </div>
          </div>
          <h1 className="mt-5 text-3xl font-bold leading-[1.15] tracking-[-0.02em] text-foreground md:text-[2.5rem]">
            The trading playbook
          </h1>
          <p className="mt-3 text-lg leading-relaxed text-muted">
            How a day trader enters the market, and exactly how this system copies that.
          </p>
        </header>

        {/* ── One sentence ───────────────────────────────────────────────── */}
        <aside aria-label="The plan in one sentence" className="mt-8 rounded-2xl border border-border border-l-[3px] border-l-accent bg-surface px-6 py-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">The whole plan in one sentence</p>
          <p className="mt-2 font-sans text-lg md:text-[1.1875rem] font-semibold leading-snug text-foreground">
            Pick from today's liquid coins, trade <em>with</em> the 4-hour trend, enter on a 1-hour pullback after a
            confirming candle closes, risk 0.5% of the account, only take trades that pay at least 1.5× the risk,
            and stop for the day after 3 trades or 3R lost.
          </p>
          <p className="mt-3 text-[0.9375rem] text-muted">
            Everything else on this page is detail. If a trade doesn't fit that sentence, we don't take it.
          </p>
        </aside>

        {/* ── The funnel ─────────────────────────────────────────────────── */}
        <Section id="funnel" n={1} title="The funnel" sub="How a signal is born: five checks, in this order. A coin must pass each one before the next is even asked.">
          <Funnel />
          <ol className="mt-7 list-decimal space-y-4 pl-6 marker:font-semibold marker:text-muted">
            <li><B>Today's coins.</B> Once a day at 00:00 UTC the desk re-picks the coins it watches, and Telegram scans the same list. A coin makes it only if it is liquid enough to fill near the screen price (at least <span className="num">$150M</span> of futures volume in 24 hours), moves enough to pay the fees (an average 1-hour range of at least <span className="num">0.6%</span> of price, which is why BTC itself usually fails), has at least a year of history, and moves with BTC: a coin running on huge volume while ignoring BTC is usually a pump. Stablecoins are skipped and the <span className="num">20</span> biggest by volume win. Settings › Day-trading coins shows today's list and why each coin is on it.</li>
            <li><B>4h bias.</B> Day traders don't guess direction; they borrow it from a bigger timeframe. If the 4h EMAs are stacked up and making higher highs, we only look for longs. Bearish → only shorts. Flat → we mostly sit out.</li>
            <li><B>1h setup.</B> Inside that direction we wait for price to come back to "value" — the EMA20/50 zone — without breaking the last swing. Chasing a candle that already ran is how beginners buy tops.</li>
            <li><B>Trigger.</B> A 1h candle must <em>close</em> as a hammer / engulfing / break of the previous bar's high. A half-formed candle can reverse — so the desk evaluates closed candles only, and nothing new can appear until the next :00. Only the 1-hour candle counts: a hammer on the 5m or 15m chart is not a trigger. While a coin is Almost, the desk shows <em>"If this hour closed now"</em>: the engine run on the candle still forming, so you can watch the trigger take shape. It is a preview, never a signal.</li>
            <li><B>Plan + filters.</B> Stop behind the swing, TP1 at the next level, reward ≥ 1.5× risk, fees ≤ 25% of the risk unit, room before the next key level, BTC not trending against an altcoin trade, and the confluence score must agree. Fail any one → no trade.</li>
          </ol>
          <p className="mt-6"><B>Room to move.</B> Price often stalls at yesterday's high and low and at the week's opening price (the <em>PD high</em>, <em>PD low</em> and <em>Week open</em> lines on the chart). A long that enters just under one of them starts out pushing against a wall, so the engine wants at least <span className="num">0.5R</span> of open space before the first one (below the entry for a short). Measured on <span className="num">18</span> coins over two separate 180-day windows: those entries won only <span className="num">20–37%</span> of the time and lost <span className="num text-loss">−0.18R</span> to <span className="num text-loss">−0.55R</span> a trade. Refusing them moved the engine from <span className="num">−0.03R</span> to <span className="num">+0.05R</span> a trade over the year. That is better, but it is still not a proven edge.</p>
          <SignalRules />
        </Section>

        {/* ── The setups ─────────────────────────────────────────────────── */}
        <Section id="setups" n={2} title="The setups we recognise" sub="Two trade shapes the desk takes, and one it ignores on purpose.">
          <div className="space-y-14">
            <div>
              <SetupHeader icon={<TrendingUp className="size-5 text-profit" />} title="Trend pullback" tag="Primary · live" color="success" />
              <Figure caption="An uptrend dips into the EMA zone, a hammer closes, and the trade enters at that close with the stop under the swing.">
                <PullbackDiagram />
              </Figure>
              <p className="mt-6"><B>What it is.</B> The market is trending, takes a breath (2–5 red candles back into the EMA zone), holds above the last swing low, then prints a green candle that says "buyers are back".</p>
              <SubHead>The checklist</SubHead>
              <Rules rules={[
                "4h bias aligned with the trade direction",
                "1h EMA20 above EMA50 (for longs)",
                "Price within 1.5 ATR of EMA20 or EMA50",
                "A real pullback: ≥ 0.8 ATR off the recent high",
                "Pullback low stays above the prior swing low",
                "Trigger: bullish engulfing, hammer, or close above the previous high",
                "Volume on trigger ≥ 0.5× average (≥ 1.2× = A+)",
                "RSI 35–60: reset, not overbought (30–65 still passes, graded B+)",
                "Room: no key level (yesterday's high/low, the week's open) less than 0.5R above the entry",
              ]} />
              <Levels items={[
                ["Stop", "below the pullback low minus 0.25 ATR."],
                ["TP1", "nearest resistance (or 1.5R)."],
                ["TP2", "2.5R."],
              ]} />
              <p className="mt-5 text-[0.9375rem]"><B>Measured:</B> <span className="num font-semibold text-profit">+0.11R</span> net per trade (PF <span className="num">1.23</span>, <span className="num">37</span> trades) in the July 2026 test, but <span className="num font-semibold text-loss">−0.38R</span> (PF <span className="num">0.44</span>, <span className="num">32</span> trades) on the next 90 days. The edge did not hold out of sample, so treat every card as a candidate to check, not a trade to take.</p>
            </div>

            <div>
              <SetupHeader icon={<Shield className="size-5 text-warning" />} title="Range fade" tag="Secondary · live" color="warning" />
              <Figure caption="Price bounces between a floor and a ceiling; a long-wick rejection at the floor is the long entry, aiming for the middle.">
                <RangeDiagram />
              </Figure>
              <p className="mt-6"><B>What it is.</B> Price has bounced between the same floor and ceiling at least twice each. At the floor, a candle with a long lower wick shows sellers failed — we buy the floor and aim for the middle of the range.</p>
              <SubHead>The checklist</SubHead>
              <Rules rules={[
                "Range at least 1.5 ATR tall, ≥ 2 touches of each side in 30 bars",
                "Price within 1 ATR of the boundary you're fading",
                "Rejection wick > 1.2× the candle body",
                "RSI not pushing against the fade (long: < 60; A+ wants ≤ 30)",
                "4h not trending hard against you",
                "Room: no key level less than 0.5R past the entry, toward the target",
              ]} />
              <Levels items={[
                ["Stop", "0.5 ATR beyond the boundary."],
                ["TP1", "range midpoint."],
                ["TP2", "the opposite boundary."],
              ]} />
            </div>

            <div>
              <SetupHeader icon={<Ban className="size-5 text-danger" />} title="Breakout retest" tag="Disabled" color="danger" />
              <p className="mt-4">
                Level breaks on volume, price comes back to test it, holds, we enter. It is a classic — and in our backtests it lost money everywhere
                (profit factor <span className="num">0.3–0.5</span> over ~<span className="num">100</span> trades, every timeframe and quality bucket). The code still measures it so we notice if that changes,
                but the desk will never show it.
              </p>
              <Alert status="default" className="mt-5">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Lesson</Alert.Title>
                  <Alert.Description>A setup being famous is not evidence it works on this market with these costs.</Alert.Description>
                </Alert.Content>
              </Alert>
            </div>
          </div>
        </Section>

        {/* ── Trading patterns ───────────────────────────────────────────── */}
        <Section id="patterns" n={3} title="Trading patterns" sub="What the candles say, and how much that is worth on its own.">
          <PatternLibrary />
        </Section>

        {/* ── Trade management ───────────────────────────────────────────── */}
        <Section id="managing" n={4} title="Managing the trade" sub="The same five steps, every time.">
          <ol className="list-decimal space-y-4 pl-6 marker:font-semibold marker:text-muted">
            <li><B>Enter at the close of the trigger candle</B>, at market. If price has already moved more than 0.3 ATR past that close, or two 1h candles (2 hours) have passed, the setup is gone — wait for the next one. Telegram prints both limits for you.</li>
            <li><B>Stop-loss goes in the same second as the entry.</B> It sits behind the level that would prove the idea wrong, never at a round "2%" number.</li>
            <li><B>At TP1, take half off and move the stop to break-even.</B> From here the trade cannot lose; the rest is a free shot at TP2.</li>
            <li><B>Time stop.</B> If a 1h setup hasn't reached TP1 or the stop within 24h (36h for range fades), close it. Good setups move; stale ones tie up risk. Telegram gives the clock time, and the tracker closes the signal at that same time.</li>
            <li><B>Never widen a stop. Never add to a loser.</B> You can only move the stop in the direction of profit.</li>
          </ol>
          <SignalAnatomy />
        </Section>

        {/* ── Risk ───────────────────────────────────────────────────────── */}
        <Section id="risk" n={5} title="Risk" sub="The part that keeps you in the game.">
          <div className="space-y-5">
            <p><B>Risk a fixed 0.5% per trade.</B> That is the desk default (Settings › Account) and the one figure every screen sizes from: the ticket, the Pro desk header, the safety limit, and the practice trades the engine logs for itself. Ten losses in a row — which <em>will</em> happen at a 50% win rate — costs ~5%, not your account. Never go above 1% while you are learning.</p>
            <div>
              <p><B>Position size is derived, never chosen:</B></p>
              <div className="num mt-3 rounded-xl border border-border bg-surface px-4 py-3.5 text-sm leading-7 text-foreground">
                <div>risk $ = account × 0.5%</div>
                <div>units  = risk $ ÷ |entry − stop|</div>
                <div className="mt-1 text-muted">e.g. $500 × 0.5% = $2.50; entry 2,400, stop 2,370 → $30 per unit → 0.083 ETH</div>
              </div>
            </div>
            <p>A wider stop means a <em>smaller</em> position, not more risk. The desk does this maths for you on every card.</p>
            <p><B>Three trades a day, 3R a day, then you are done.</B> The desk counts real trades only (practice trades and WATCH ideas don't count), each open trade reserves 1R, and the day resets at 00:00 UTC. Once today's losses plus open risk reach <span className="num">3R</span>, or you have taken <span className="num">3</span> trades, the desk shows <Chip size="sm" color="danger" variant="soft" className="align-middle font-semibold">LOCKED</Chip>. Why three: the best configuration ever found produced about 3 signals a <em>week</em>, so a fourth trade in one day means you went looking for one. A real trade logged while LOCKED is still recorded — the journal must stay honest — but it is marked as breaking the limit on the Trades page and in its review, and the next Telegram scan report repeats the lock. Change the numbers in Settings › Account, never after a bad day.</p>
            <p><B>Costs are a gate, not a footnote.</B> Fees + slippage + funding are charged in R. A stop that's 0.2% wide on a 1.5R target pays ~0.5R in costs — a coin-flip that loses. We reject any plan where costs exceed 25% of the risk unit.</p>
            <p><B>Expect any edge to be small, slow and fragile.</B> The best configuration so far made ~+0.1R a trade over ~3 trades a week, then lost −0.38R a trade over the following 90 days. Anything promising more is usually curve-fit; size small until a setup proves itself on data it wasn't tuned on.</p>
            <p><B>Automatic brakes:</B> a coin whose signals won less than 40% of the time over the last 30 days (with at least 8 of them) is paused; account drawdown past the throttle tiers shrinks size and finally pauses trading.</p>
          </div>
        </Section>

        {/* ── Reading the chart ──────────────────────────────────────────── */}
        <Section id="chart" n={6} title="Reading the chart" sub="Every line on the desk chart, the rule behind it, and exactly when to buy and when to sell.">
          <p>
            The desk chart draws the engine's rules, so you can check a setup with your own eyes. The picture is a trend pullback at the
            moment its trigger candle closes. Each number on it is one of the six checks below, and all six must be true when a 1-hour candle closes.
          </p>
          <Figure wide caption="Price on top with the two EMAs, the key levels and the trade plan. Volume with its 20-hour average in the middle. RSI with its reset band at the bottom. Labels sit on the right, as on the desk.">
            <ChartReadingDiagram />
          </Figure>

          <SubHead>The six checks</SubHead>
          <NumberedChecks items={[
            { title: "Trend", body: <>EMA 20 above EMA 50: the blue line over the purple one means the 1-hour trend is up. The 4-hour bias in the desk header has to point the same way. Blue under purple means only sells, and tangled lines mean sit out.</> },
            { title: "Pullback into the zone", body: <>Price comes back into the shaded zone between the two lines, at least <span className="num">0.8</span> ATR below its high, without breaking the last swing low. This is buying the dip, not the top.</> },
            { title: "RSI back in the band", body: <>In the RSI pane the line is back inside the shaded band, <span className="num">35</span> to <span className="num">60</span> for a buy. Above 60 the move has already run. Below 35 the trend may be breaking. Just outside the band still passes, graded B+.</> },
            { title: "A trigger candle closes", body: <>A 1-hour hammer, a bullish engulfing, or a close above the previous candle's high, marked BUY on the desk. It only counts once it has closed, so a hammer on the 5-minute chart is not a trigger.</> },
            { title: "Volume confirms it", body: <>The trigger's volume bar reaches at least half of the grey average line. At <span className="num">1.2</span> times the average or more it is an A+ setup. The legend prints the number, for example <em>Vol 1.4× avg</em>.</> },
            { title: "Room to move", body: <>The first key level above the entry, yesterday's high (PD high), yesterday's low (PD low) or the week's open, is at least half a risk unit away. Price often stalls at those lines. In the picture the bracket shows about two risk units up to PD high.</> },
          ]} />

          <SubHead>When to buy</SubHead>
          <div className="space-y-4">
            <p>
              When all six checks pass at a 1-hour close, the desk card turns <B>Ready</B> and Telegram sends the plan with exact prices: the Entry,
              Stop and Target lines in the picture. Buy at that candle's close, sized from the stop so a stop-out costs your risk per trade (section 5).
              Two things cancel the entry: price already past the red dotted <em>don't chase</em> line, or two hours gone since the signal. Then let it go and wait for the next setup.
            </p>
            <p>
              The engine also runs checks the chart can't show: its confluence score, fees under a quarter of the risk, the coin's recent record,
              and the coin's recent record. That is why a chart that looks perfect can still be Blocked. A signal that goes against a strong BTC trend
              is not blocked: it comes labelled <B>Against BTC · half risk</B>, and the ticket sizes it at half your usual risk. Trust the card, not the picture.
            </p>
          </div>

          <SubHead>When to sell</SubHead>
          <Levels items={[
            ["Stop", "below the pullback low, with a quarter-ATR buffer. If it is hit, sell everything. Place it the moment you buy, and never move it further away."],
            ["TP1", "the next resistance above, at least 1.5× the risk. In the picture that is the last swing high. Sell half there and move the stop up to your entry: the rest can no longer lose."],
            ["TP2", "2.5× the risk. Sell the rest."],
            ["Time", "if neither the stop nor a target is hit within 24 hours (36 for a range fade), sell at the market price."],
          ]} />

          <SubHead>What the engine ignores</SubHead>
          <div className="space-y-4">
            <p>
              VWAP, and the extra levels behind <em>show all</em> (VWAP bands, the Asia range, the opening range), are there for your own reading.
              No rule uses them, so a signal can fire just under VWAP, as it does in the picture. The only levels in the engine's rules are
              yesterday's high and low and the week's open.
            </p>
            <p>
              A sell trade is the mirror image: EMA 20 below EMA 50, a bounce up into the zone, RSI back between <span className="num">40</span> and{" "}
              <span className="num">65</span>, a shooting star, a bearish engulfing or a close below the previous low, the stop above the bounce high,
              and the targets below.
            </p>
          </div>
        </Section>

        {/* ── Market structure ───────────────────────────────────────────── */}
        <Section id="structure" n={7} title="Market structure" sub="Higher highs, higher lows, and what a broken trend really tells you.">
          <p>
            Price never moves in a straight line. It climbs to a top, a <B>swing high</B>, falls back to a bottom, a <B>swing low</B>, and
            starts again. Compare each swing with the one of the same kind before it and the chart tells you its trend.
          </p>
          <SwingLabels />
          <p className="mt-5">
            Higher highs and higher lows are an <B>uptrend</B>. Lower highs and lower lows are a <B>downtrend</B>. When the highs and the
            lows disagree, there is <B>no clear trend</B>: price is moving sideways.
          </p>

          <Figure wide caption="An uptrend in its channel holds while price stays above its last higher low (the dotted line). Then a close under it (1), the failed bounce that makes a lower high (2), and the lower low that carries the downtrend on (3).">
            <StructureDiagram />
          </Figure>

          <SubHead>How the chart draws it</SubHead>
          <div className="space-y-4">
            <p>
              Press the <B>Structure</B> button in the chart's toolbar, on any timeframe. Every swing gets its label, joined by a dotted line
              that follows price. A swing only counts once price has turned <span className="num">2.5</span> normal candles (ATR) away from it,
              so small wiggles are ignored. The move still running ends in a question mark, like <em>LL?</em>, because it can still change.
            </p>
            <p>
              In a trend the chart also draws its <B>channel</B>: a line through the last two higher lows with a parallel line through the
              highest high since (lower highs and the lowest low in a downtrend). One line names the swing the trend must hold, for
              example <em>Uptrend holds above 4.951</em>, and the legend gives the verdict: Uptrend, Downtrend, No clear trend, or broken.
            </p>
          </div>

          <SubHead>Where it fits our trades</SubHead>
          <div className="space-y-4">
            <p>
              A trend pullback (section 2) is buying a <B>higher low</B>. Price dips into the zone between the EMAs and stops above the last
              swing low, which is the desk's <em>Higher low intact</em> check. The stop goes just under that low, because if it breaks there is
              no higher low any more and the reason for the trade is gone. Structure tells you <em>where</em> to buy, and the 1-hour trigger
              candle tells you <em>when</em>.
            </p>
            <p>
              Read the bigger timeframe for direction and the smaller one for timing. On 8 October NEAR's <Tf>1h</Tf> uptrend was intact,
              holding above <span className="num">4.951</span>, while its <Tf>15m</Tf> trend broke under <span className="num">5.266</span>.
              That small break was the pullback into the 1-hour buy zone, not a reason to sell. The 4-hour bias in the desk header is a
              separate check with its own, smaller swings, so it can disagree with the Structure line on the 4-hour chart.
            </p>
          </div>

          <SubHead>A broken trend is not a sell signal</SubHead>
          <p>
            A close under the last higher low breaks an uptrend. Many traders sell right there. We checked what price did next.
          </p>
          <Table className="mt-5 rounded-xl border border-border">
            <Table.ScrollContainer>
              <Table.Content aria-label="What price did after a structure break" className="min-w-[560px] text-sm">
                <Table.Header>
                  <Table.Column isRowHeader>On the 1-hour chart</Table.Column>
                  <Table.Column className="text-right">Times</Table.Column>
                  <Table.Column className="text-right">Lower a day later</Table.Column>
                  <Table.Column className="text-right">Fell 1 ATR before rising 1 ATR</Table.Column>
                </Table.Header>
                <Table.Body>
                  <BreakRow c="An uptrend broke" n="944" lower="50%" first="50%" />
                  <BreakRow c="Any hour, for comparison" n="280,000" lower="52%" first="51%" />
                  <BreakRow c="A downtrend broke" n="927" lower="56%" first="51%" />
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
          <p className="mt-4">
            If a break told you the next move, the first row would sit well above the comparison and the last one well below it. After an
            uptrend broke, price was lower a day later exactly half the time: a coin flip. After a downtrend broke, price even kept falling a
            little more often. A break tells you the old reason to trade is gone. It does not tell you the new direction.
          </p>
          <p className="mt-3 text-sm text-muted">
            Measured on 8 October 2026 over every 1-hour candle of 22 coins from April 2025 to October 2026, with the Structure button's own
            swing rule and only the swings that were already confirmed at that hour.
          </p>

          <SubHead>After a break, step by step</SubHead>
          <NumberedChecks items={[
            { title: "Stop buying", body: <>A close under the last higher low ends the pattern of higher lows. Take no new buys on that timeframe, but don't sell just because of the break. The low it makes is the first lower low.</> },
            { title: "Wait for a lower high", body: <>Price usually bounces. If the bounce turns down below the last high, that is a lower high (LH). Now the highs and the lows are both falling, and the legend says Downtrend.</> },
            { title: "Let the lower lows confirm it", body: <>Each new lower low carries the downtrend on, until a close above the last lower high breaks it the same way. The system still only sells when the 4-hour trend points down and a sell setup forms.</> },
          ]} />

          <SubHead>Continuation or reversal?</SubHead>
          <p>
            Every pullback ends one of two ways. Price makes a new high and the trend goes on (<B>continuation</B>), or it closes under the
            last higher low and turns (<B>reversal</B>). Traders read clues to guess which. We tested the usual ones: after each new high in
            a 1-hour uptrend, did price make another new high before it closed under the higher low?
          </p>
          <Table className="mt-5 rounded-xl border border-border">
            <Table.ScrollContainer>
              <Table.Content aria-label="Continuation clues against chance" className="min-w-[560px] text-sm">
                <Table.Header>
                  <Table.Column isRowHeader>Clue when the pullback starts</Table.Column>
                  <Table.Column className="text-right">New high came first</Table.Column>
                  <Table.Column className="text-right">Chance alone</Table.Column>
                  <Table.Column className="text-right">Difference</Table.Column>
                </Table.Header>
                <Table.Body>
                  <ClueRow c="Every pullback (2,927)" got="52%" chance="48%" diff="+4" />
                  <ClueRow c="4-hour trend agrees / disagrees" got="53% / 50%" chance="50% / 45%" diff="+3 / +5" />
                  <ClueRow c="RSI divergence / none" got="44% / 56%" chance="42% / 51%" diff="+2 / +5" />
                  <ClueRow c="Last leg on weaker / stronger volume" got="52% / 51%" chance="48% / 47%" diff="+4 / +4" />
                  <ClueRow c="Shallow pullback / deep pullback" got="67% / 37%" chance="64% / 33%" diff="+3 / +3" />
                  <ClueRow c="Every good clue / every bad clue" got="68% / 31%" chance="65% / 29%" diff="+3 / +2" />
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
          <div className="mt-4 space-y-4">
            <p>
              <em>Chance alone</em> is what price would do if it moved at random: it reaches whichever line is nearer more often. A shallow pullback is still
              close to the old high, so it makes a new high more often, and that explains almost all of its 67% against 37%. No clue, and no mix
              of clues, moved the odds more than a few points beyond chance. Downtrends came out the same.
            </p>
            <p>
              The odds and the payout also balance each other. Near the high a new high is likely, but there is little left to gain. Deep in
              the pullback the reward is bigger, and a reversal is more likely too. So instead of guessing, plan for both outcomes:
            </p>
          </div>
          <div className="mt-4" />
          <NumberedChecks items={[
            { title: "Decide where you are wrong first", body: <>For a buy, just under the higher low. If the move turns out to be a reversal, the stop takes 1R and you are out early.</> },
            { title: "Let the market say which it was", body: <>A new high confirms continuation. A close under the higher low, then a lower high, confirms reversal. Until one of them happens, it is an undecided pullback.</> },
            { title: "Make the winners bigger than the losers", body: <>The system's plan does this: the stop under the higher low, half at target 1 (at least 1.5R) and the rest at target 2 (2.5R). You don't need to know which way it goes, only that a wrong guess costs less than a right one pays.</> },
          ]} />
          <p className="mt-4 text-sm text-muted">
            Measured on 8 October 2026: 2,927 pullbacks in 1-hour uptrends and 3,227 in downtrends, 22 coins, April 2025 to October 2026,
            with the Structure button's swing rule. A new high counts when price touches it; a reversal needs a 1-hour close under the higher low.
          </p>

          <SubHead>Traps</SubHead>
          <dl className="divide-y divide-separator border-y border-separator">
            <Trap t="Selling the first break" looks="Price closes under the last higher low and you sell straight away." stop="The first table in this section: what follows a break is a coin flip. The desk only sells with a falling 4-hour trend and a sell setup." />
            <Trap t="Trusting the channel line" looks="Price pokes through the channel's lower line and you call the trend over." stop="The channel is drawn through just two swings. The legend only says broken after a close under the last higher low." />
            <Trap t="Acting on a ? label" looks="The newest label reads LL? and you trade it." stop="It is not a swing until price turns 2.5 normal candles away. Until then the label can change." />
            <Trap t="Reading one timeframe" looks="The 15-minute trend broke, so you think the move is over." stop="Direction from the bigger timeframe (4h, then 1h), timing from the smaller one. A small break inside a bigger uptrend is often the pullback the setup waits for." />
          </dl>
        </Section>

        {/* ── Timeframes ─────────────────────────────────────────────────── */}
        <Section id="timeframes" n={8} title="Why 1-hour entries" sub="With a 4-hour bias, and not 15 minutes.">
          <Table className="rounded-xl border border-border">
            <Table.ScrollContainer>
              <Table.Content aria-label="Backtest comparison by configuration" className="min-w-[600px] text-sm">
                <Table.Header>
                  <Table.Column isRowHeader>Config (net of costs)</Table.Column>
                  <Table.Column className="text-right">Avg R / trade</Table.Column>
                  <Table.Column className="text-right">Profit factor</Table.Column>
                  <Table.Column className="text-right">Win rate</Table.Column>
                  <Table.Column className="text-right">Verdict</Table.Column>
                </Table.Header>
                <Table.Body>
                  <Row c="15m entries, all setups" r="−0.44" pf="< 1" wr="—" v="Stops too tight; costs eat 0.5–1.2R" bad />
                  <Row c="1h entries, all setups" r="−0.09" pf="0.9" wr="~48%" v="Breakout retest drags it under" bad />
                  <Row c="1h, trend pullback + range fade, cost gate (Jul)" r="+0.11" pf="1.23" wr="51%" v="Live config, in sample" good />
                  <Row c="Same live config, next 90 days (Sep)" r="−0.38" pf="0.44" wr="28%" v="Did not hold out of sample" bad />
                  <Row c="Re-check, today's engine, 15m entries (Apr–Oct 2026)" r="−0.18" pf="0.73" wr="39%" v="2.4 trades a day, −76R over 6 months" bad />
                  <Row c="Re-check, today's engine, 15m entries (Oct 2025–Apr 2026)" r="−0.04" pf="0.94" wr="44%" v="Still under the 1h result" bad />
                  <Row c="Today's 1h live config, same 14 coins (Apr–Oct 2026)" r="+0.17" pf="1.33" wr="48%" v="0.9 trades a day; −0.02R the 6 months before" good />
                  <Row c="Same 1h config, never-tested Apr–Oct 2025" r="−0.39" pf="0.46" wr="29%" v="Lost in both halves: price went against the signals" bad />
                  <Row c="Same 1h config, all 18 months (Apr 2025–Oct 2026)" r="−0.06" pf="0.90" wr="40%" v="440 trades: no proven edge yet" bad />
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
          <p className="mt-6">
            On <Tf>15m</Tf> the "perfect" A+ setups had the tightest stops — and tight stops are the ones round-trip costs destroy. The <Tf>1h</Tf> chart gives
            stops room to breathe and keeps you to a few decisions a day. It is the only timeframe where our data ever showed an edge, and that edge has not held up since, so it is the least-bad choice, not a proven one. On 6 Oct 2026 the 15-minute test was run again with today's engine, because July's engine had bugs since fixed. Stops are now wider (about 1.3% instead of 0.1–0.3%), but 15-minute entries still lost in both 6-month periods, even before fees in the recent one. The extra trades are not better trades.
          </p>
          <p className="mt-4">
            On 7 Oct 2026 the buy and sell signals were checked over 18 months instead of 6, including April–October 2025, a period no earlier test had
            used. The live setup lost badly there, and over the whole 18 months it averages −0.06R a trade, so treat every signal as practice until
            something beats that. Nine changes were written down before any test ran: skip the "close beyond the last candle" trigger, trade only with
            the coin's daily trend, only with BTC's, only coins stronger than BTC, no weekends, four ways to take profit, and "trade only while the
            engine is on a winning run". Two looked good on the two recent periods and then failed on 2025, so none went live.
          </p>
          <p className="mt-4">
            Buy and sell signals did equally well in 2026 and equally badly in 2025. Neither side is the better one; the market's mood decides, and
            none of the filters could tell the mood in advance. Every chart was also turned upside down to check that a buy and a sell follow the same
            rules. One part of the confluence score, Wyckoff, did not: it called "accumulation" near the lows more easily than "distribution" near the
            highs. A mirror-exact version was built and tested, gave the same results within noise, and is not live.
          </p>
          <Alert status="accent" className="mt-5">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Rule</Alert.Title>
              <Alert.Description>
                No gate or timeframe changes without a fresh <code className="num rounded bg-default px-1.5 py-0.5 text-[0.8125rem] text-foreground">bun run backtest</code> report to justify them.
              </Alert.Description>
            </Alert.Content>
          </Alert>
        </Section>

        {/* ── Routine ────────────────────────────────────────────────────── */}
        <Section id="routine" n={9} title="A day at the desk" sub="Five minutes in, two minutes an hour, five minutes out.">
          <div className="space-y-3">
            <Routine icon={<Clock className="size-5 text-pop-cyan" />} title="Session start" time="5 min" items={["Read the context strip: BTC 4h bias, BTC gate, macro.", "Decide: longs only / shorts only / sit out today.", "Check today's safety limit: trades and R left. Then glance at today's coins — who is Almost?"]} />
            <Routine icon={<Crosshair className="size-5 text-warning" />} title="Every hour at :00" time="2 min" items={["The desk re-scans ~20s after each 1h close.", "Ready card → follow the 5 execution steps exactly. LOCKED → you are done for today, even on a Ready card.", "Almost → set a price alert at the trigger level and walk away. Blocked → skip it."]} />
            <Routine icon={<Calculator className="size-5 text-profit" />} title="Session end" time="5 min" items={["Log every trade taken with the button on its card, real or practice.", "Review Performance: did you follow the plan, not did you win? A 'broke limit' tag is the first thing to fix.", "Never change rules after one bad day."]} />
          </div>
        </Section>

        {/* ── Habits ─────────────────────────────────────────────────────── */}
        <Section id="habits" n={10} title="What profitable traders do" sub="Mostly they wait. The rules below are what they do instead of trading.">
          <p>
            Profitable day traders are not the ones who guess the market best. They pick one kind of setup, and most of their day is
            waiting for it. When it comes, they follow the same plan every time, and when it doesn't, they don't trade. Ten rules
            sum it up, and the desk already enforces most of them for you.
          </p>
          <SubHead>The ten rules</SubHead>
          <NumberedChecks items={[
            { title: "Wait for your setup", body: <>No setup, no trade, even if that is all day. A day with zero trades is a good day: nothing was lost and you are ready for tomorrow. On the desk only a <em>Ready</em> card is a trade.</> },
            { title: "Wait for the candle to close", body: <>A candle can look perfect halfway through and be the opposite by the close. Only a closed 1-hour candle is a trigger. The 15-minute and 5-minute charts are fine for <em>watching</em> how price reaches the zone, but they never decide the trade. A 15-minute confirmation step is a new rule that has not been tested, and it usually means entering later at a worse price.</> },
            { title: "Know the exit before the entry", body: <>Stop, target 1, target 2 and the time stop are written down before you click. If you can't say where you are wrong, don't trade. Every Ready card and Telegram signal gives all four.</> },
            { title: "Risk the same small amount every time", body: <>Risk <span className="num">0.5%</span> of the account on each trade, every trade. Don't size up after a win to press your luck, or after a loss to win it back. The ticket sizes it for you (section 5).</> },
            { title: "Never chase", body: <>If price has run past the <em>Don't chase</em> line, or two hours have passed since the trigger closed, the trade is gone. Let it go. Another setup will come, and the one you chased is how beginners buy tops.</> },
            { title: "Stop when the day's limit is hit", body: <>After <span className="num">3</span> trades, or <span className="num">3R</span> lost, you are done until 00:00 UTC. The desk shows LOCKED. The trade right after a loss, taken to win it back, is the most expensive one there is.</> },
            { title: "Manage by the plan, not by feel", body: <>Take half at target 1 and move the stop to break-even. Never widen a stop and never add to a losing trade. Closing early because you are nervous turns a plan with an edge into a guess (section 4).</> },
            { title: "Trade with the bigger trend", body: <>Longs only when the 4-hour trend is up, shorts only when it is down. When BTC is moving hard the other way, a signal comes labelled <em>Against BTC</em>: take it at half size, never full. Fighting BTC is riskier, because altcoins usually follow it.</> },
            { title: "Log every trade and judge the process", body: <>Log every trade, win or lose, and ask "did I follow the plan?" before "did I win?". A rule-following loss is a good trade. A rule-breaking win is a bad one, because it teaches you to break the rule again. The Trades page tags trades that broke the limit.</> },
            { title: "Change the rules only with data", body: <>Never change a rule after one bad day, or one good one. A rule changes only when a backtest on months of data says the new version is better, the way the score floor was lowered from 45 to 40.</> },
          ]} />

          <SubHead>The traps that cost beginners the most</SubHead>
          <dl className="divide-y divide-separator border-y border-separator">
            <Trap t="The boredom trade" looks="Nothing has signalled for hours, so you take something that almost fits." stop="Only Ready cards are trades. Almost and Waiting are not." />
            <Trap t="The revenge trade" looks="You just lost and want it back right now, often with a bigger size." stop="Fixed 0.5% risk on the ticket, and LOCKED after 3 trades or 3R." />
            <Trap t="Chasing (FOMO)" looks="The coin is flying and you buy because you are afraid to miss it." stop="The Don't chase line, and the two-hour limit after the trigger." />
            <Trap t="Moving the stop" looks="Price is near your stop, so you move it further away to give it room." stop="The stop is set with the entry, and it only ever moves toward profit." />
            <Trap t="Deciding early" looks="You buy before the hour closes because the candle looks good." stop="The desk only signals on a closed 1-hour candle. The preview is labelled as a preview." />
            <Trap t="Changing the system" looks="After a losing week you switch indicators, timeframes or coins." stop="No rule changes without a backtest report (section 8)." />
          </dl>

          <SubHead>What normal looks like</SubHead>
          <div className="space-y-4">
            <p><B>Winning half the time is normal.</B> The money comes from winners that are bigger than the losers (every plan needs a target at least 1.5 times the risk), not from being right often.</p>
            <p><B>Losing streaks will happen.</B> Over <span className="num">100</span> trades at a <span className="num">50%</span> win rate, a run of <span className="num">5</span> losses in a row happens about 8 times in 10, and <span className="num">6</span> in a row more often than not. At <span className="num">0.5%</span> a trade, 6 losses cost about 3% of the account. Small risk is what lets you keep following the plan through a streak.</p>
            <p><B>Few trades is normal.</B> The <span className="num">3</span> a day is a ceiling, not a target. The engine has averaged about one signal a day across all of today's coins, and some days it sends none.</p>
          </div>
          <Alert status="warning" className="mt-6">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Be honest about the edge</Alert.Title>
              <Alert.Description>
                Waiting and discipline only make money when the setup you wait for wins over time. Our backtests have not proven that yet:
                the latest 180-day tests came out close to break-even. Follow these rules at practice size and let the trade log, not hope, decide when to size up.
              </Alert.Description>
            </Alert.Content>
          </Alert>
        </Section>

        {/* ── Glossary ───────────────────────────────────────────────────── */}
        <Section id="glossary" n={11} title="Words on the desk" sub="Plain meanings for the terms you'll see on the desk.">
          <dl className="divide-y divide-separator border-y border-separator">
            <G t="R" d="One unit of risk = the distance from entry to stop. A +2R trade made twice what it risked. We measure everything in R so small and large coins compare." />
            <G t="Against BTC · half risk" d="A signal that passed every rule but goes against a strong BTC trend: a buy while BTC falls hard, or a sell while it rises hard. Altcoins usually follow BTC, so it trades at half your usual risk. Tested over 18 months, these came out about even, so the Trades page tracks them apart: if they keep losing after 20 or more have closed, they get switched off." />
            <G t="ATR" d="Average True Range — how far a 1h candle typically travels. Used to size zones and stop buffers so rules adapt to each coin's volatility." />
            <G t="EMA20 / EMA50" d="Exponential moving averages of the last 20 / 50 closes. Trend = fast above slow. The space between them is the pullback 'value zone'." />
            <G t="RSI" d="Momentum oscillator 0–100, in its own pane under the chart. For a buy we want it reset, inside the shaded 35–60 band: sellers have pushed, but not so far that the trend is broken. Sells use 40–65." />
            <G t="Volume × avg" d="The trigger candle's volume divided by the average of the 20 hours before it, drawn as the grey line through the volume bars. At least 0.5× counts; 1.2× or more makes an A+ setup." />
            <G t="Don't chase" d="The short red dotted line just past the entry of a live signal, 0.3 ATR beyond it. If price is already past it, or two hours have passed, skip the trade. Telegram prints the same price." />
            <G t="Hammer / Engulfing" d="Reversal shapes. Hammer: long lower wick, small body near the top. Bullish engulfing: a green body that swallows the prior red body. All ten patterns, with how they measured, are in section 3." />
            <G t="Swing high / low" d="A top price turned down from, or a bottom it turned up from. The Structure button counts one once price has moved 2.5 normal candles (ATR) away from it. Until then its label ends in a question mark." />
            <G t="HH · HL · LH · LL" d="Each swing compared with the one of the same kind before it: higher high and higher low (an uptrend), lower high and lower low (a downtrend). Section 7." />
            <G t="Structure break" d="A close under the last higher low in an uptrend, or over the last lower high in a downtrend. It ends the pattern, not the move: measured on 18 months of 1-hour candles, what came next was a coin flip (section 7)." />
            <G t="Continuation / reversal" d="How a pullback ends. Continuation: the trend makes a new high (a new low in a downtrend). Reversal: price closes past the last higher low and then fails at a lower high. You only know which once it happens: section 7 shows no clue tells you sooner." />
            <G t="HTF bias" d="Higher-timeframe direction (4h for our 1h entries). We borrow direction from it and never trade against it." />
            <G t="Desk stages" d="Waiting: trend is set, price not in the entry zone yet. Almost: price is in the zone and every rule that can be checked before the trigger already passes, including the engine's score, reward ≥ 1.5× the risk, fees and room before the next key level, so only the 1-hour trigger candle is missing (when the score needs a pattern's vote, the desk says the trigger must be a hammer or engulfing). Ready: a real trade, every rule passed. Blocked: the checklist passed but the engine said no, so don't trade. No setup: a required rule fails, the engine's own rules included. The chart only draws a buy or sell zone for Waiting and Almost." />
            <G t="Key levels" d="Yesterday's high and low (PD high and PD low on the chart, UTC day) and the week's opening price (Monday 00:00 UTC). Many traders watch them, so price often pauses or turns there. The engine needs 0.5R of room before the first one in the trade's path." />
            <G t="If this hour closed now" d="On an Almost coin, the desk runs the engine on the 1-hour candle that is still forming, every 30 seconds: does it count as a trigger yet, and would the engine send a signal if it closed right now? The candle can still change, so it's a preview. Only the closed candle makes a signal." />
            <G t="Checklist vs engine" d="The checklist grades the setup in the direction of the 4h trend. The engine then votes on direction from all indicators and runs extra checks (reward vs risk, fees, room, score). Only the engine creates trades, so a full checklist can still be Blocked." />
            <G t="Confluence score" d="A 0–100 vote across regime, patterns, indicators, Fibonacci, Wyckoff and Elliott. Must be ≥ 40 with a clear directional margin before the setup check even runs. Lowered from 45 on 6 Oct 2026: on two separate 180-day tests the extra trades it lets in won on average, and nothing else got worse." />
            <G t="Today's coins" d="The 20 coins the desk and Telegram watch today, re-picked at 00:00 UTC: at least $150M of futures volume, an average 1-hour range of at least 0.6%, a year of history, and moving with BTC. Section 1 has the reasons." />
            <G t="LOCKED" d="Today's safety limit is used up: 3 real trades taken, or 3R lost counting open risk. The desk stays locked until 00:00 UTC. A real trade logged while locked is recorded but marked as breaking the limit." />
          </dl>
        </Section>

        {/* ── Day trading ────────────────────────────────────────────────── */}
        <Section id="day-trading" n={12} title="Day trading" sub="Tools, not signals.">
          <Alert status="warning">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>There is no intraday signal on this desk.</Alert.Title>
              <Alert.Description>
                Measured over the last 90 days, the 1h swing pipeline loses about <span className="num">−0.38R</span> per trade net of costs,
                and capping holds to 8–12h did not fix it. No intraday signal will be shipped until one backtests positive after fees and slippage.
                The day-trader panels on the desk are decision-support only: they show where you are and what you can afford to lose, not what to trade.
              </Alert.Description>
            </Alert.Content>
          </Alert>
          <ol className="mt-7 list-decimal space-y-4 pl-6 marker:font-semibold marker:text-muted">
            <li><B><Tf>1h</Tf> for context, <Tf>5m</Tf> / <Tf>15m</Tf> for timing.</B> Read direction and the big levels on the 1h chart. Drop to 5m or 15m only to time an entry at a level you already picked — never to find a reason to trade.</li>
            <li><B>Sessions matter.</B> Asia (<span className="num">00–07</span> UTC) usually builds a range; London (<span className="num">07–13</span>) and New York (<span className="num">13–20</span>) bring the volume that breaks or rejects it; the late session (<span className="num">20–24</span>) is thin. The session clock shows where you are and when funding (<span className="num">00/08/16</span> UTC) settles. For our 1-hour signals the session made no reliable difference: over a year on today's coins, Asia signals did as well as London and New York ones, so the engine takes signals in every session. Late signals lost a little in both halves of the year, but on only <span className="num">31</span> trades, which is too few to act on.</li>
            <li><B>VWAP and prior-day levels are the map.</B> Daily VWAP with its ±1σ/±2σ bands, prior-day high/low/close, the weekly open and the Asia range are where intraday traders act. The levels table shows each one's distance in % and in 1h ATR, with the nearest level above and below highlighted. A level is a place to watch, not an entry.</li>
            <li><B>Size from the stop, check the costs.</B> Use the position-size calculator: entry and stop give the quantity for a 1R loss. On tight intraday stops, fees and slippage can eat a big share of R; if costs are above <span className="num">0.25R</span>, the stop is too tight to be worth it.</li>
            <li><B>The daily limit is final.</B> Once today's losses plus open risk use up the budget (<span className="num">3R</span> by default), or you have taken the day's <span className="num">3</span> trades, the desk shows <Chip size="sm" color="danger" variant="soft" className="align-middle font-semibold">LOCKED</Chip> until 00:00 UTC. Stop trading. A real trade logged after that is marked as breaking the limit, and the next Telegram report repeats the lock. Revenge trades are how day traders blow up.</li>
            <li><B>Flat by the end of the day.</B> An intraday trade that is still open at the close has become a swing trade you didn't plan. Close it.</li>
          </ol>
        </Section>
      </article>

      {/* ── On this page: a side list on wide screens, a drawer below that ── */}
      <nav aria-label="On this page" className="hidden xl:block">
        <div className="sticky top-20 pt-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">On this page</div>
          <TocList active={active} onPick={(id) => scrollToSection(id)} />
          <BackToTop />
        </div>
      </nav>

      {/* Below xl the side list has no room: a floating Contents button, within thumb reach. */}
      <Button
        isIconOnly
        variant="secondary"
        aria-label="On this page: open the list of sections"
        onPress={() => setTocOpen(true)}
        className="fixed right-4 z-30 size-12 rounded-full border border-border shadow-lg xl:hidden bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-11"
      >
        <TableOfContents className="size-5" />
      </Button>
      <TocDrawer
        open={tocOpen}
        onOpenChange={setTocOpen}
        active={active}
        onPick={(id) => {
          setTocOpen(false);
          afterDrawerCloses(() => scrollToSection(id));
        }}
      />
    </div>
  );
}

// ─── on this page ────────────────────────────────────────────────────────────

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Run `fn` once the contents drawer has fully closed. While it is open the
 * page's scroll is locked, and unlocking puts the old position back: a jump
 * made before that is undone. Checked every 50 ms, for at most 1.5 s.
 */
function afterDrawerCloses(fn: () => void, startedAt = Date.now()) {
  const open = document.querySelector('[role="dialog"][aria-label="On this page"]');
  if (!open || Date.now() - startedAt > 1500) {
    setTimeout(fn, 30);
    return;
  }
  setTimeout(() => afterDrawerCloses(fn, startedAt), 50);
}

/** The section list: numbered sections, unnumbered parts under them; the one being read is lit. */
function TocList({ active, onPick, large }: { active: string; onPick: (id: string) => void; large?: boolean }) {
  return (
    <ol className="space-y-0.5 border-l border-separator">
      {TOC.map((t, i) => (
        <li key={t.id}>
          <a
            href={`#${t.id}`}
            onClick={(e) => {
              e.preventDefault();
              onPick(t.id);
            }}
            aria-current={active === t.id ? "true" : undefined}
            className={cn(
              "-ml-px flex gap-2 border-l-2 leading-snug outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus",
              large ? "py-2.5 text-[0.9375rem]" : "py-1.5 text-sm",
              "sub" in t ? cn("pl-9", large ? "text-sm" : "text-[0.8125rem]") : "pl-3.5",
              active === t.id
                ? "border-accent font-semibold text-foreground"
                : "border-transparent text-muted hover:text-foreground",
            )}
          >
            {tocNumber(i) !== null && <span className="num w-4 shrink-0 text-right text-xs leading-5 opacity-70">{tocNumber(i)}</span>}
            {t.label}
          </a>
        </li>
      ))}
    </ol>
  );
}

function BackToTop({ onDone }: { onDone?: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
        if (!onDone) return toTop();
        onDone();
        afterDrawerCloses(toTop);
      }}
      className="mt-5 pl-4 text-sm text-muted outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-focus"
    >
      ↑ Back to top
    </button>
  );
}

/** "On this page" in a drawer, for screens too narrow for the side list. */
function TocDrawer({ open, onOpenChange, active, onPick }: {
  open: boolean; onOpenChange: (open: boolean) => void; active: string; onPick: (id: string) => void;
}) {
  return (
    <Drawer.Backdrop isOpen={open} onOpenChange={onOpenChange}>
      <Drawer.Content placement="right">
        <Drawer.Dialog className="w-[19rem] max-w-[85vw]" aria-label="On this page">
          <Drawer.CloseTrigger />
          <Drawer.Header>
            <Drawer.Heading className="text-xs font-semibold uppercase tracking-wider text-muted">On this page</Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body className="pb-6">
            <TocList active={active} onPick={onPick} large />
            <BackToTop onDone={() => onOpenChange(false)} />
          </Drawer.Body>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

// ─── layout bits ─────────────────────────────────────────────────────────────

function Section({ id, n, title, sub, children }: { id: string; n: number; title: string; sub?: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="mt-16 scroll-mt-20 border-t border-separator pt-10">
      <p className="num text-sm font-semibold text-accent-soft-foreground">{String(n).padStart(2, "0")}</p>
      <h2 id={`${id}-h`} className="mt-1 text-2xl md:text-[1.625rem] font-bold leading-tight tracking-[-0.015em] text-foreground">
        {title}
      </h2>
      {sub && <p className="mt-2 text-base md:text-[1.0625rem] text-muted">{sub}</p>}
      <div className="mt-8">{children}</div>
    </section>
  );
}
function B({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>;
}
/** Small uppercase label above a list inside a section. */
function SubHead({ children }: { children: ReactNode }) {
  return <h4 className="mb-3 mt-6 text-[0.8125rem] font-semibold uppercase tracking-wider text-muted">{children}</h4>;
}
/** A diagram as an article figure: capped width so its labels stay ~14px, caption underneath. */
function Figure({ caption, children, wide }: { caption: string; children: ReactNode; wide?: boolean }) {
  return (
    <figure className="mt-5">
      <div className={wide ? "w-full overflow-x-auto" : "max-w-md"}>{children}</div>
      <figcaption className={cn("mt-2.5 text-sm leading-relaxed text-muted", !wide && "max-w-md")}>{caption}</figcaption>
    </figure>
  );
}
/** Timeframe token, e.g. 1h / 15m. */
function Tf({ children }: { children: ReactNode }) {
  return <Kbd variant="light" className="num align-baseline text-[0.8125rem]"><Kbd.Content>{children}</Kbd.Content></Kbd>;
}
function SetupHeader({ icon, title, tag, color }: { icon: ReactNode; title: string; tag: string; color: "success" | "warning" | "danger" }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {icon}
      <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground">{title}</h3>
      <Chip size="sm" variant="soft" color={color} className="font-semibold">{tag}</Chip>
    </div>
  );
}
function Rules({ rules }: { rules: string[] }) {
  return (
    <ol className="list-decimal space-y-1.5 pl-6 marker:text-muted">
      {rules.map((r, i) => <li key={i}>{r}</li>)}
    </ol>
  );
}
function Levels({ items }: { items: Array<[string, string]> }) {
  return (
    <dl className="mt-5 grid grid-cols-[3.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-xl border border-border bg-surface px-4 py-3.5 text-[0.9375rem]">
      {items.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className={cn("font-semibold", k === "Stop" ? "text-loss" : k === "Time" ? "text-muted" : "text-profit")}>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
/** Numbered checks; the numbers match the marks on the diagram above them. */
function NumberedChecks({ items }: { items: Array<{ title: string; body: ReactNode }> }) {
  return (
    <ol className="space-y-4">
      {items.map((it, i) => (
        <li key={it.title} className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-3">
          <span aria-hidden className="num mt-1 flex size-6 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background">{i + 1}</span>
          <p><B>{it.title}.</B> {it.body}</p>
        </li>
      ))}
    </ol>
  );
}
function Row({ c, r, pf, wr, v, good, bad }: { c: string; r: string; pf: string; wr: string; v: string; good?: boolean; bad?: boolean }) {
  const tone = good ? "text-profit" : bad ? "text-loss" : "";
  return (
    <Table.Row>
      <Table.Cell className="text-foreground">{c}</Table.Cell>
      <Table.Cell className={cn("num text-right font-semibold", tone)}>{r}</Table.Cell>
      <Table.Cell className="num text-right">{pf}</Table.Cell>
      <Table.Cell className="num text-right">{wr}</Table.Cell>
      <Table.Cell className={cn("text-right", tone)}>{v}</Table.Cell>
    </Table.Row>
  );
}
function Routine({ icon, title, time, items }: { icon: ReactNode; title: string; time: string; items: string[] }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-5 py-4">
      <div className="flex items-center gap-2.5">
        {icon}
        <h3 className="font-sans text-base md:text-[1.0625rem] font-semibold text-foreground">{title}</h3>
        <span className="ml-auto rounded-full bg-default px-2.5 py-0.5 text-xs font-medium text-muted">{time}</span>
      </div>
      <ol className="mt-3 list-decimal space-y-1.5 pl-6 text-[0.9375rem] marker:text-muted">{items.map((t, i) => <li key={i}>{t}</li>)}</ol>
    </div>
  );
}
/** One beginner trap: what it looks like, and what on the desk stops it. */
function Trap({ t, looks, stop }: { t: string; looks: string; stop: string }) {
  return (
    <div className="grid gap-1 py-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-6">
      <dt className="font-sans font-semibold text-foreground">{t}</dt>
      <dd>
        {looks}
        <span className="mt-1 block text-[0.9375rem] text-muted"><span className="font-semibold text-profit">Stops it here:</span> {stop}</span>
      </dd>
    </div>
  );
}
function G({ t, d }: { t: string; d: string }) {
  return (
    <div className="grid gap-1 py-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-6">
      <dt className="font-sans font-semibold text-foreground">{t}</dt>
      <dd>{d}</dd>
    </div>
  );
}

/**
 * One Telegram signal, line by line, mapped to the rule it comes from. The
 * scanner, the desk and the chart all show these same numbers for a signal
 * (backend autoScanner.formatSignalMessage + signal_performance).
 */
function SignalAnatomy() {
  const lines: Array<[string, ReactNode]> = [
    ["🟢 BUY AVAXUSDT · Trend pullback ⭐ A", <>The side, the coin, which setup it is (section 2) and its grade (A+ is best; B+ has one soft miss). <B>Buy</B> = buy now to sell higher; <B>sell</B> = short, the reverse. This is the line your phone's notification shows.</>],
    ["Entry $20.00\nStop $19.00 (−5.0%)\nTarget 1 $21.60 (+8.0%)\nTarget 2 $22.50 (+12.5%)", <>Buy price, the stop where the idea is proven wrong, and the two targets, each with what it does to the trade.</>],
    ["💵 Risk $5.00 if the stop is hit · target 1 pays 1.6× that", <>Your risk per trade from the risk settings (section 5): size the position from the stop so a loss costs this much. Target 1 never pays less than 1.5× the risk.</>],
    ["1. Buy near $20.00. Don't chase: skip it if price is already above $20.15 or it's past Oct 5, 15:00 UTC.", <>Step 1 above, as numbers: 0.3 ATR past the entry, or two hours. The desk switches the signal to <em>Missed</em> or <em>Expired</em> at the same moment.</>],
    ["2. At target 1, close half and move the stop to your entry ($20.00).\n3. Close the rest at target 2, or by Oct 6, 13:00 UTC (24h time limit).", <>Steps 3 and 4 above, with the exact clock time to close the trade if neither a target nor the stop is hit.</>],
    ["📖 Learn: Playbook › 2. The setups › Trend pullback", <>Where this setup is explained, so every signal is also a lesson.</>],
    ["Fired by: hammer · Coin: trending up · BTC: bullish", <>The candle that fired it, then the coin's and BTC's trend. On its own a pattern is a coin flip (section 3); here it only times a trade the trend and the zone already allow.</>],
  ];
  return (
    <div className="mt-12">
      <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground">Reading a signal message</h3>
      <p className="mt-3">
        Every Telegram signal is this plan written out. The desk card and the chart show the same numbers for it, and the
        tracker grades it with the same rules: stop checked first, half at TP1, time stop on the clock.
      </p>
      <figure className="mt-5 overflow-hidden rounded-2xl border border-border bg-surface">
        <figcaption className="border-b border-separator px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted">
          Example message, line by line
        </figcaption>
        <dl className="divide-y divide-separator">
          {lines.map(([line, meaning]) => (
            <div key={line} className="grid gap-1.5 px-5 py-4 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] sm:gap-6">
              <dt className="num whitespace-pre-line text-[0.8125rem] leading-relaxed text-foreground">{line}</dt>
              <dd className="text-[0.9375rem]">{meaning}</dd>
            </div>
          ))}
        </dl>
      </figure>
    </div>
  );
}

/** Which section is currently at the top of the viewport (for the sticky TOC). */
function useScrollSpy(ids: readonly string[]) {
  const [active, setActive] = useState<string>(ids[0] ?? "");
  const key = ids.join("|");
  useEffect(() => {
    const els = key.split("|").map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el);
    if (els.length === 0 || typeof IntersectionObserver === "undefined") return;
    // Callbacks only carry the sections whose visibility changed, so keep the
    // full picture: a long section that stays in view must stay active.
    const inBand = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) inBand.add(e.target.id);
          else inBand.delete(e.target.id);
        }
        // Between sections (or above the first): the last one that started above the band.
        const current = els.find((el) => inBand.has(el.id))
          ?? els.filter((el) => el.getBoundingClientRect().top < 72).at(-1)
          ?? els[0];
        if (current) setActive(current.id);
      },
      { rootMargin: "-72px 0px -60% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [key]);
  return active;
}

/** "N min read" from the article's own text (~220 words a minute). */
function useReadingMinutes(ref: React.RefObject<HTMLElement | null>) {
  const [minutes, setMinutes] = useState(0);
  useEffect(() => {
    const words = ref.current?.innerText.split(/\s+/).filter(Boolean).length ?? 0;
    setMinutes(Math.max(1, Math.round(words / 220)));
  }, [ref]);
  return minutes;
}

// ─── diagrams (inline SVG, theme colours via CSS vars) ───────────────────────

function Funnel() {
  const steps = [
    { t: "Today's coins", s: "liquidity" },
    { t: "4h bias", s: "direction" },
    { t: "1h pullback", s: "location" },
    { t: "Trigger close", s: "timing" },
    { t: "Plan & filters", s: "R:R · costs · room · BTC" },
  ];
  return (
    <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5" aria-label="Signal funnel">
      {steps.map((st, i) => (
        <li key={i} className="relative rounded-xl border border-border bg-surface px-3 py-3.5 text-center">
          <div className="num text-xs font-semibold text-accent-soft-foreground">Step {i + 1}</div>
          <div className="mt-1 font-sans text-[0.9375rem] font-semibold leading-snug text-foreground">{st.t}</div>
          <div className="mt-0.5 text-[0.8125rem] leading-snug text-muted">{st.s}</div>
          {i < steps.length - 1 && (
            <span aria-hidden className="absolute -right-[11px] top-1/2 z-10 hidden -translate-y-1/2 text-base text-muted md:block">›</span>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Uptrend → pullback into EMA zone → hammer → stop/targets. y grows downward. */
function PullbackDiagram() {
  // hand-placed candles (x, open, close, high, low)
  const cs: Array<[number, number, number, number, number]> = [
    [14, 150, 138, 134, 154], [28, 138, 128, 124, 142], [42, 128, 118, 114, 131],
    [56, 118, 108, 104, 122], [70, 108, 96, 92, 112], [84, 96, 84, 80, 100],
    [98, 84, 92, 82, 95], [112, 92, 102, 90, 105], [126, 102, 110, 100, 113],
    [140, 110, 108, 107.5, 130], // hammer
    [154, 108, 96, 92, 112], [168, 96, 84, 80, 100],
  ];
  return (
    <svg viewBox="0 0 260 200" className="h-auto w-full rounded-xl border border-border bg-surface">
      {/* EMA zone */}
      <path d="M0 160 C 60 120, 120 100, 260 70" stroke="var(--chart-ema-fast)" strokeWidth={1.5} fill="none" opacity={0.9} />
      <path d="M0 175 C 60 140, 120 118, 260 92" stroke="var(--chart-ema-slow)" strokeWidth={1.5} fill="none" opacity={0.9} />
      <text x={196} y={14} fontSize={8} fill="var(--chart-ema-fast)">EMA20</text>
      <text x={228} y={14} fontSize={8} fill="var(--chart-ema-slow)">EMA50</text>
      {cs.map(([x, o, c, h, l], i) => <Candle key={i} x={x} o={o} c={c} h={h} l={l} />)}
      {/* annotations */}
      <text x={60} y={14} fontSize={8} fill="var(--muted)">① trend up</text>
      <text x={112} y={76} fontSize={8} fill="var(--muted)" textAnchor="middle">② pullback</text>
      <text x={140} y={146} fontSize={8} fill="var(--profit)" textAnchor="middle">③ hammer</text>
      <line x1={186} x2={256} y1={108} y2={108} stroke="var(--foreground)" strokeDasharray="3 2" strokeWidth={1} />
      <text x={188} y={105} fontSize={8} fill="var(--foreground)">entry</text>
      <line x1={186} x2={256} y1={134} y2={134} stroke="var(--loss)" strokeDasharray="3 2" strokeWidth={1} />
      <text x={188} y={144} fontSize={8} fill="var(--loss)">stop (under swing)</text>
      <line x1={186} x2={256} y1={68} y2={68} stroke="var(--profit)" strokeDasharray="3 2" strokeWidth={1} />
      <text x={188} y={65} fontSize={8} fill="var(--profit)">TP1 1.5R+</text>
    </svg>
  );
}

function RangeDiagram() {
  const cs: Array<[number, number, number, number, number]> = [
    [14, 120, 70, 62, 124], [28, 70, 60, 56, 76], [42, 60, 90, 58, 94], [56, 90, 128, 86, 132],
    [70, 128, 138, 124, 146], [84, 138, 110, 104, 142], [98, 110, 72, 66, 114], [112, 72, 62, 58, 80],
    [126, 62, 86, 60, 90], [140, 86, 120, 84, 126], [154, 120, 140, 118, 146], [168, 140, 118, 110, 144],
    [182, 118, 66, 58, 122], [196, 66, 96, 60, 100], [210, 96, 134, 92, 138],
    [224, 137, 131, 129, 158], // rejection wick through the floor, close back inside
  ];
  return (
    <svg viewBox="0 0 260 200" className="h-auto w-full rounded-xl border border-border bg-surface">
      <line x1={4} x2={256} y1={58} y2={58} stroke="var(--loss)" strokeDasharray="3 2" strokeWidth={1} />
      <text x={6} y={54} fontSize={8} fill="var(--loss)">ceiling (3 touches)</text>
      <line x1={4} x2={256} y1={146} y2={146} stroke="var(--profit)" strokeDasharray="3 2" strokeWidth={1} />
      <text x={6} y={158} fontSize={8} fill="var(--profit)">floor (3 touches)</text>
      <line x1={4} x2={256} y1={102} y2={102} stroke="var(--muted)" strokeDasharray="2 3" strokeWidth={1} />
      <text x={256} y={98} fontSize={8} fill="var(--muted)" textAnchor="end">TP1 = mid</text>
      {cs.map(([x, o, c, h, l], i) => <Candle key={i} x={x} o={o} c={c} h={h} l={l} />)}
      <line x1={200} x2={256} y1={168} y2={168} stroke="var(--loss)" strokeDasharray="3 2" strokeWidth={1} />
      <text x={214} y={180} fontSize={8} fill="var(--profit)" textAnchor="end">wick rejection → long</text>
      <text x={256} y={192} fontSize={8} fill="var(--loss)" textAnchor="end">stop 0.5 ATR under floor</text>
    </svg>
  );
}

/**
 * The desk chart, annotated: a trend pullback the moment its trigger candle
 * closes. Price pane (EMAs, key levels, the plan), volume with its 20-hour
 * average, RSI with the reset band, labels on the right like the desk. The
 * numbered marks are the six checks in "Reading the chart". y grows downward.
 */
function ChartReadingDiagram() {
  // [x, open, close, high, low]: an uptrend, a pullback into the EMA zone, then a hammer.
  const cs: Array<[number, number, number, number, number]> = [
    [14, 172, 168, 165, 176], [28, 168, 160, 157, 171], [42, 160, 150, 147, 163], [56, 150, 142, 139, 153],
    [70, 142, 134, 131, 145], [84, 134, 140, 131, 146], [98, 140, 146, 137, 150], [112, 146, 134, 132, 149],
    [126, 134, 120, 117, 137], [140, 120, 108, 105, 123], [154, 108, 112, 102, 116], [168, 112, 96, 93, 114],
    [182, 96, 74, 66, 98], // the last swing high: target 1
    [196, 74, 86, 70, 89], [210, 86, 98, 83, 101], [224, 98, 108, 95, 112], [238, 108, 120, 104, 124],
    [252, 120, 118, 115, 126],
    [266, 118, 115, 113, 141], // the hammer: small body on top, long lower wick
  ];
  const vol = [12, 10, 13, 11, 12, 9, 10, 11, 14, 13, 9, 12, 15, 11, 9, 8, 9, 7, 15];
  const rsi = [55, 58, 60, 62, 63, 55, 50, 57, 61, 64, 62, 66, 70, 62, 55, 50, 45, 44, 49];

  const LANE = 374, X0 = 266; // label lane; the trigger candle
  const ENTRY = 115, STOP = 146, TP1 = 66, TP2 = 38, CHASE = 104;
  const PD_HIGH = 52, VWAP = 92, WEEK = 160, PD_LOW = 184;
  const volY = (h: number) => 232 - h * 1.6;
  const rsiY = (v: number) => 242 + ((80 - v) / 60) * 48; // RSI 80 at the top of its pane, 20 at the bottom

  // EMAs from the closes: a fast and a slow average, like EMA 20 and EMA 50.
  const closes = cs.map(c => c[2]);
  const ema = (alpha: number, seed: number) => { let e = seed; return closes.map(c => (e += alpha * (c - e))); };
  const fast = ema(0.25, 172), slow = ema(0.1, 178);
  const line = (ys: Array<number | null>) =>
    cs.flatMap(([x], i) => (ys[i] == null ? [] : [`${x},${ys[i]!.toFixed(1)}`])).join(" ");
  const zoneFrom = 13; // the pullback: from the candle after the swing high
  const zone = [
    ...cs.slice(zoneFrom).map(([x], k) => `${x},${fast[zoneFrom + k]!.toFixed(1)}`),
    ...cs.slice(zoneFrom).map(([x], k) => `${x},${slow[zoneFrom + k]!.toFixed(1)}`).reverse(),
  ].join(" ");
  const avg = vol.map((_, i) => (i >= 3 ? volY(vol.slice(0, i).reduce((a, b) => a + b, 0) / i) : null));

  return (
    <svg
      viewBox="0 0 440 296"
      role="img"
      aria-label="Example 1-hour chart: an uptrend pulls back into the zone between EMA 20 and EMA 50, RSI drops into its band, and a hammer closes on rising volume, with the entry, stop and targets drawn to the right"
      className="h-auto w-full min-w-[36rem] rounded-xl border border-border bg-surface"
    >
      {/* pane dividers */}
      <line x1={0} x2={440} y1={194} y2={194} stroke="var(--border)" />
      <line x1={0} x2={440} y1={236} y2={236} stroke="var(--border)" />

      {/* key levels across the chart */}
      {([[PD_HIGH, "var(--chart-prior-day)"], [VWAP, "var(--chart-vwap)"], [WEEK, "var(--chart-week)"], [PD_LOW, "var(--chart-prior-day)"]] as const).map(([y, color]) => (
        <line key={y} x1={0} x2={LANE} y1={y} y2={y} stroke={color} strokeWidth={0.8} opacity={0.85} />
      ))}

      {/* the zone between the EMAs, where the pullback ends */}
      <polygon points={zone} fill="var(--profit)" opacity={0.12} />
      <polyline points={line(slow)} fill="none" stroke="var(--chart-ema-slow)" strokeWidth={1.4} strokeLinejoin="round" />
      <polyline points={line(fast)} fill="none" stroke="var(--chart-ema-fast)" strokeWidth={1.4} strokeLinejoin="round" />
      <g fontSize={6.5} fill="var(--muted)">
        <line x1={8} x2={18} y1={12} y2={12} stroke="var(--chart-ema-fast)" strokeWidth={1.6} />
        <text x={21} y={14.3}>EMA 20</text>
        <line x1={52} x2={62} y1={12} y2={12} stroke="var(--chart-ema-slow)" strokeWidth={1.6} />
        <text x={65} y={14.3}>EMA 50</text>
      </g>

      {cs.map(([x, o, c, h, l]) => <Candle key={x} x={x} o={o} c={c} h={h} l={l} w={8} />)}

      {/* the trigger candle and its BUY mark */}
      <rect x={X0 - 7} y={110} width={14} height={34} rx={3} fill="none" stroke="var(--foreground)" strokeWidth={0.8} strokeDasharray="2 2" opacity={0.6} />
      <rect x={X0 - 13} y={150} width={26} height={11} rx={3} fill="var(--profit)" />
      <text x={X0} y={158} fontSize={6.5} fontWeight={700} fill="var(--surface)" textAnchor="middle">BUY</text>

      {/* the plan, from the trigger to the labels */}
      <line x1={182} x2={X0} y1={TP1} y2={TP1} stroke="var(--profit)" strokeWidth={0.8} strokeDasharray="1 3" opacity={0.6} />
      <line x1={X0} x2={LANE - 2} y1={TP2} y2={TP2} stroke="var(--profit)" strokeWidth={0.9} strokeDasharray="2 3" />
      <line x1={X0} x2={LANE - 2} y1={TP1} y2={TP1} stroke="var(--profit)" strokeWidth={0.9} strokeDasharray="5 4" />
      <line x1={X0} x2={LANE - 2} y1={ENTRY} y2={ENTRY} stroke="var(--foreground)" strokeWidth={0.9} opacity={0.7} />
      <line x1={X0} x2={LANE - 2} y1={STOP} y2={STOP} stroke="var(--loss)" strokeWidth={0.9} strokeDasharray="5 4" />
      <line x1={X0} x2={X0 + 28} y1={CHASE} y2={CHASE} stroke="var(--loss)" strokeWidth={1.2} strokeDasharray="1 2.5" />
      <text x={X0 + 31} y={CHASE + 2.3} fontSize={6.5} fill="var(--loss)">don't chase</text>

      {/* room before the first key level above the entry */}
      <path d={`M${LANE - 14} ${ENTRY - 1} h-4 V${PD_HIGH + 1} h4`} fill="none" stroke="var(--muted)" strokeWidth={0.8} />

      {/* volume and its average */}
      <text x={6} y={205} fontSize={6.5} fill="var(--muted)">Volume</text>
      {cs.map(([x, o, c], i) => (
        <rect key={x} x={x - 4} y={volY(vol[i]!)} width={8} height={232 - volY(vol[i]!)} fill={c <= o ? "var(--profit)" : "var(--loss)"} opacity={0.45} />
      ))}
      <polyline points={line(avg)} fill="none" stroke="var(--foreground)" strokeWidth={0.8} opacity={0.5} />
      <text x={X0 + 22} y={215} fontSize={6.5} fill="var(--profit)">1.4× avg</text>

      {/* RSI with the reset band */}
      <rect x={0} y={rsiY(60)} width={LANE} height={rsiY(35) - rsiY(60)} fill="var(--warning)" opacity={0.12} />
      <line x1={0} x2={LANE} y1={rsiY(60)} y2={rsiY(60)} stroke="var(--warning)" strokeWidth={0.6} strokeDasharray="2 2" opacity={0.6} />
      <line x1={0} x2={LANE} y1={rsiY(35)} y2={rsiY(35)} stroke="var(--warning)" strokeWidth={0.6} strokeDasharray="2 2" opacity={0.6} />
      <polyline points={line(rsi.map(rsiY))} fill="none" stroke="var(--warning)" strokeWidth={1.2} strokeLinejoin="round" />
      <text x={6} y={247} fontSize={6.5} fill="var(--muted)">RSI 14</text>
      <text x={LANE + 3} y={rsiY(70) + 2} fontSize={6} fill="var(--muted)">70</text>
      <text x={LANE + 3} y={rsiY(60) + 2} fontSize={6} fill="var(--muted)">60</text>
      <text x={LANE + 3} y={rsiY(35) + 2} fontSize={6} fill="var(--muted)">35</text>

      {/* labels on the right, like the desk */}
      <LaneTag y={TP2} text="Target 2" color="var(--profit)" />
      <LaneTag y={PD_HIGH} text="PD high" color="var(--chart-prior-day)" filled />
      <LaneTag y={TP1} text="Target 1" color="var(--profit)" />
      <LaneTag y={VWAP} text="VWAP" color="var(--chart-vwap)" filled />
      <LaneTag y={ENTRY} text="Entry" color="var(--foreground)" />
      <LaneTag y={STOP} text="Stop" color="var(--loss)" />
      <LaneTag y={WEEK} text="Week open" color="var(--chart-week)" filled />
      <LaneTag y={PD_LOW} text="PD low" color="var(--chart-prior-day)" filled />

      {/* the six checks */}
      <Mark x={154} y={137} n={1} />
      <Mark x={203} y={118} n={2} />
      <Mark x={284} y={rsiY(49) - 4} n={3} />
      <Mark x={250} y={140} n={4} />
      <Mark x={X0 + 12} y={212} n={5} />
      <Mark x={LANE - 18} y={84} n={6} />
    </svg>
  );
}

/** The four swing labels, coloured as on the chart: green where buyers lead, red where sellers do. */
function SwingLabels() {
  const items: Array<[string, string, string, boolean]> = [
    ["HH", "Higher high", "the top went above the last top: buyers pushed further.", true],
    ["HL", "Higher low", "the dip stopped above the last dip: buyers stepped in sooner. Our buys live here.", true],
    ["LH", "Lower high", "the bounce turned down below the last top: sellers stepped in sooner.", false],
    ["LL", "Lower low", "the drop went under the last dip: sellers pushed further.", false],
  ];
  return (
    <dl className="mt-5 grid grid-cols-[2.25rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-xl border border-border bg-surface px-4 py-3.5 text-[0.9375rem]">
      {items.map(([k, name, text, up]) => (
        <div key={k} className="contents">
          <dt className={cn("num font-bold", up ? "text-profit" : "text-loss")}>{k}</dt>
          <dd><B>{name}:</B> {text}</dd>
        </div>
      ))}
    </dl>
  );
}

function ClueRow({ c, got, chance, diff }: { c: string; got: string; chance: string; diff: string }) {
  return (
    <Table.Row>
      <Table.Cell className="text-foreground">{c}</Table.Cell>
      <Table.Cell className="num text-right font-semibold">{got}</Table.Cell>
      <Table.Cell className="num text-right">{chance}</Table.Cell>
      <Table.Cell className="num text-right text-muted">{diff}</Table.Cell>
    </Table.Row>
  );
}

function BreakRow({ c, n, lower, first }: { c: string; n: string; lower: string; first: string }) {
  return (
    <Table.Row>
      <Table.Cell className="text-foreground">{c}</Table.Cell>
      <Table.Cell className="num text-right">{n}</Table.Cell>
      <Table.Cell className="num text-right font-semibold">{lower}</Table.Cell>
      <Table.Cell className="num text-right">{first}</Table.Cell>
    </Table.Row>
  );
}

/**
 * Swings, uptrend to downtrend: the uptrend's channel and its last higher
 * low, the close under it (1), the lower high (2) and the next lower low (3).
 * y grows downward, so a lower price has a larger y.
 */
function StructureDiagram() {
  // [x, y, label]
  const pts: Array<[number, number, string]> = [
    [22, 168, "L"], [62, 112, "H"], [92, 140, "HL"], [136, 76, "HH"], [166, 112, "HL"], [212, 44, "HH"],
    [252, 130, "LL"], [288, 86, "LH"], [330, 152, "LL"], [368, 134, "LH"], [410, 184, "LL"],
  ];
  const tone = (l: string) => (l === "HH" || l === "HL" ? "var(--profit)" : l === "LH" || l === "LL" ? "var(--loss)" : "var(--muted)");
  // Channel: a line through the two higher lows, its parallel through the highest high since.
  const slope = (112 - 140) / (166 - 92);
  const lowAt = (x: number) => 140 + slope * (x - 92);
  const up = 44 - lowAt(212);
  const x0 = 92, x1 = 262;
  // Where the drop from the last high crosses the last higher low: the break.
  const breakX = 212 + ((112 - 44) / (130 - 44)) * (252 - 212);
  return (
    <svg
      viewBox="0 0 440 204"
      role="img"
      aria-label="A price path labelled H, HL, HH, HL, HH inside a rising channel, then a close under the last higher low, a lower high and lower lows"
      className="h-auto w-full min-w-[34rem] rounded-xl border border-border bg-surface"
    >
      <polygon
        points={`${x0},${lowAt(x0)} ${x1},${lowAt(x1)} ${x1},${lowAt(x1) + up} ${x0},${lowAt(x0) + up}`}
        fill="var(--profit)" opacity={0.08}
      />
      <line x1={x0} y1={lowAt(x0)} x2={x1} y2={lowAt(x1)} stroke="var(--profit)" strokeWidth={1.2} strokeDasharray="5 3" />
      <line x1={x0} y1={lowAt(x0) + up} x2={x1} y2={lowAt(x1) + up} stroke="var(--profit)" strokeWidth={1.2} strokeDasharray="5 3" />
      <line x1={166} x2={436} y1={112} y2={112} stroke="var(--foreground)" strokeWidth={0.8} strokeDasharray="2 3" opacity={0.7} />
      <text x={436} y={107} fontSize={7.5} fill="var(--muted)" textAnchor="end">last higher low</text>

      <polyline points={pts.map(([x, y]) => `${x},${y}`).join(" ")} fill="none" stroke="var(--foreground)" strokeWidth={1.6} strokeLinejoin="round" />
      {pts.map(([x, y, l], i) => {
        const high = i > 0 && y < pts[i - 1]![1];
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={2.2} fill={tone(l)} />
            <text x={x} y={high ? y - 6 : y + 13} fontSize={8.5} fontWeight={700} fill={tone(l)} textAnchor="middle">{l}</text>
          </g>
        );
      })}

      <text x={100} y={16} fontSize={8} fill="var(--profit)" textAnchor="middle">uptrend: buy the higher lows</text>
      <text x={352} y={16} fontSize={8} fill="var(--loss)" textAnchor="middle">downtrend: lower highs, lower lows</text>
      <Mark x={breakX + 9} y={112} n={1} />
      <Mark x={300} y={74} n={2} />
      <Mark x={344} y={160} n={3} />
    </svg>
  );
}

/** A line's label in the right-hand lane, filled for key levels and outlined for the plan, as on the desk. */
function LaneTag({ y, text, color, filled }: { y: number; text: string; color: string; filled?: boolean }) {
  return (
    <g>
      <rect x={374} y={y - 5} width={62} height={10} rx={2} fill={filled ? color : "var(--surface)"} stroke={color} strokeWidth={0.8} />
      <text x={377} y={y + 2.3} fontSize={6.5} fontWeight={600} fill={filled ? "var(--surface)" : color}>{text}</text>
    </g>
  );
}

/** A numbered mark on the diagram: the check with the same number in the list below it. */
function Mark({ x, y, n }: { x: number; y: number; n: number }) {
  return (
    <g>
      <circle cx={x} cy={y} r={6} fill="var(--foreground)" />
      <text x={x} y={y + 2.4} fontSize={7} fontWeight={700} fill="var(--surface)" textAnchor="middle">{n}</text>
    </g>
  );
}
